import classNames from 'classnames';
import { IReactionDisposer, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { Component, PureComponent } from 'react';
import { Container } from 'react-bootstrap';

import { MainSearch } from './MainSearch';
import { TVChartContainer } from '../../component/TVChartContainer';
import { Depth } from './Depth';
import { AssetIndex } from './AssetIndex';
import { TradeForm } from './TradeForm';
import { MyOrder } from './MyOrder';
import { TOKEN_DBTC_ID, TOKEN_DICP_ID } from '../../model/utils/config';
import session from '../../model/Session';
import { MarketStore } from '../../model/market';
import { DepthItem } from '../../model/canister/Depth';
import { RouteComponentProps, withRouter } from 'react-router-class-tools';

@observer
export class TradingPage extends PureComponent<
  RouteComponentProps<{ b: string; q: string }, {}, {}>
> {
  @observable
  askOrderDepthClickItem: DepthItem;
  @observable
  bidOrderDepthClickItem: DepthItem;
  @observable
  private market: MarketStore;
  @observable
  private baseTokenId: string;
  @observable
  private quoteTokenId: string;

  private bqDisposer?: IReactionDisposer;

  async componentDidMount() {
    this.bqDisposer = reaction(
      () => this.props?.match?.params,
      async p => {
        console.log(`bqDisposer ${p.b} ${p.q}`);
        let { b, q } = p;
        if (!b || !q) {
          b = TOKEN_DBTC_ID;
          q = TOKEN_DICP_ID;
        }
        if (this.baseTokenId === b && this.quoteTokenId === q) return;

        this.baseTokenId = b;
        this.quoteTokenId = q;
        this.market = new MarketStore(b, q);
        console.info(`TradingPage pair changed: market.init(${b},${q}`);
        await this.market.initialize();
      },
      {
        fireImmediately: true
      }
    );
  }

  async componentWillUnmount() {
    if (this.bqDisposer) this.bqDisposer();
    this.market.dispose();
    console.debug('TradingPage componentWillUnmount: market.dispose()');
  }

  render() {
    const { isDesktop } = session;
    const { market } = this;
    const { depth, baseToken, quoteToken, tickOf24H, marketSetting } =
      market || {};

    return (
      <Container
        as="main"
        className={classNames(
          'p-0',
          ...(session.isLaptop
            ? []
            : ['border-start', 'border-end', 'border-secondary', 'bg-dark'])
        )}
        fluid={session.isLaptop}
      >
        <div className="border-bottom border-secondary d-flex">
          {isDesktop && (
            <div className="p-0">
              <MainSearch />
            </div>
          )}
          <div className="flex-fill border-start border-end border-secondary p-0 d-flex flex-column">
            {isDesktop && (
              <>
                <AssetIndex
                  className="border-bottom border-secondary"
                  baseTokenSymbol={baseToken?.symbol}
                  baseTokenName={baseToken?.name}
                  quoteTokenSymbol={quoteToken?.symbol}
                  lastPrice={tickOf24H?.lastPrice}
                  high={tickOf24H?.high}
                  low={tickOf24H?.low}
                  volume={tickOf24H?.volume}
                  open={tickOf24H?.open}
                  close={tickOf24H?.close}
                  fiatPrice={tickOf24H?.lastPrice}
                  fiatCurrency="USD"
                />
                <div style={{ height: 390 }}>
                  <TVChartContainer
                    symbol={`${baseToken?.symbol}/${quoteToken?.symbol}`}
                    klineCanisterId={marketSetting?.klineCanisterId}
                    priceDecimals={marketSetting?.priceDecimals}
                    baseTokenDecimals={baseToken?.decimals}
                  />
                </div>
              </>
            )}
            <TradeForm
              className="flex-fill"
              marketId={this.market?.marketId}
              priceDecimals={marketSetting?.priceDecimals}
              baseToken={baseToken}
              quoteToken={quoteToken}
              baseTokenPrecision={marketSetting?.baseTokenPrecision}
              quoteTokenPrecision={marketSetting?.quoteTokenPrecision}
              maxAskPriceLimit={marketSetting?.maxAskPriceLimit}
              maxBidPriceLimit={marketSetting?.maxBidPriceLimit}
              orderPricePrecision={marketSetting?.orderPricePrecision}
              orderVolumePrecision={marketSetting?.orderVolumePrecision}
              latestPrice={this.market?.latestPrice?.price}
              bidOrderDepthClickItem={this.bidOrderDepthClickItem}
              askOrderDepthClickItem={this.askOrderDepthClickItem}
              onAskOrderSubmitted={this.market?.submitAskLimitOrder}
              onBidOrderSubmitted={this.market?.submitBidLimitOrder}
            />
          </div>
          <div>
            <Depth
              levels={marketSetting?.depthLevels}
              count={depth?.count}
              level={depth?.level}
              type={depth?.type}
              latestPrice={this.market?.latestPrice}
              orderPricePrecision={marketSetting?.orderPricePrecision}
              depthVolumePrecision={marketSetting?.depthVolumePrecision}
              baseTokenPrecision={marketSetting?.baseTokenPrecision}
              baseTokenSymbol={baseToken?.symbol}
              quoteTokenSymbol={quoteToken?.symbol}
              askOrders={depth?.askOrders}
              bidOrders={depth?.bidOrders}
              onChangeDepthLevel={depth?.switchLevel}
              onChangeDepthType={depth?.switchType}
              onAskOrderClick={item => {
                this.askOrderDepthClickItem = item;
              }}
              onBidOrderClick={item => {
                this.bidOrderDepthClickItem = item;
              }}
            />
          </div>
        </div>
        <MyOrder
          myOrderList={this.market?.myOpenOrders}
          tradeHistoryList={this.market?.tradeHistory?.tradeHistoryList}
          baseTokenSymbol={baseToken?.symbol}
          baseTokenDecimals={baseToken?.decimals}
          quoteTokenSymbol={quoteToken?.symbol}
          quoteTokenDecimals={quoteToken?.decimals}
          baseTokenPrecision={marketSetting?.baseTokenPrecision}
          quoteTokenPrecision={marketSetting?.quoteTokenPrecision}
          onSwitchOnlyCurrencyMarket={() => {}}
          onOrderCancelled={async orderId => {
            await this.market?.cancelOrder(orderId);
          }}
          onCancelAllClicked={async () => {
            await this.market?.cancelAllOrders();
          }}
        />
      </Container>
    );
  }
}

export default withRouter(TradingPage);
